/**
 * 권한(Permission) 관련 타입 정의
 *
 * nGle-Works ERP의 권한 시스템에서 사용되는
 * 권한 키(PermissionKey)와 역할별 권한 매핑을 정의합니다.
 */

import { RoleId } from './role';

/**
 * 권한 키 타입
 *
 * 시스템에서 사용하는 모든 권한을 문자열 리터럴로 정의합니다.
 * 형식: '<영역>.<동작>'
 *
 * @example
 * 'dashboard.read' - 대시보드 조회
 * 'notice.write' - 공지사항 작성
 * 'hr.manage' - 인사관리 전체 권한
 */
export type PermissionKey =
  // 공통 메뉴 권한
  | 'dashboard.read'         // 대시보드 조회
  | 'notice.read'            // 공지사항 조회
  | 'notice.write'           // 공지사항 작성/수정
  | 'organization.read'      // 조직도 조회
  | 'organization.write'     // 조직도 수정

  // 근로관리 권한
  | 'work.my.read'           // 내 근로 정보 조회
  | 'work.my.write'          // 내 근로 정보 수정 (출퇴근 기록 등)
  | 'work.team.read'         // 팀원 근로 정보 조회
  | 'work.all.read'          // 전사 근로 정보 조회
  | 'work.approve'           // 근무/연차 승인 처리

  // 인사관리 권한
  | 'hr.employees.read'      // 직원 정보 조회
  | 'hr.employees.write'     // 직원 정보 수정
  | 'hr.org.read'            // 조직 정보 조회
  | 'hr.org.write'           // 조직 정보 수정
  | 'hr.settings.read'       // 인사 설정 조회
  | 'hr.settings.write'      // 인사 설정 수정
  | 'hr.manage'              // 인사관리 전체 권한

  // 팀관리 권한
  | 'team.my.read'           // 내 팀 정보 조회
  | 'team.my.manage'         // 내 팀 관리
  | 'team.all.read'          // 전체 팀 정보 조회

  // MM관리 권한
  | 'mm.read'                // MM 현황 조회
  | 'mm.register'            // MM 등록
  | 'mm.manage'              // MM 관리 전체 (조직장)

  // 마스터데이터 권한
  | 'master.read'            // 마스터데이터 조회
  | 'master.write'           // 마스터데이터 수정

  // 증명서 발급 권한
  | 'certificate.request'    // 증명서 신청 (모든 직원)
  | 'certificate.manage'     // 증명서 관리 (HR)

  // 평가 권한
  | 'evaluation.self'        // 본인 평가 조회/제출 (전직원)
  | 'evaluation.manage'      // 평가 관리 (HR)

  // 시스템 권한
  | 'system.admin';          // 시스템 관리 전체

/**
 * 역할별 권한 매핑
 *
 * 각 역할이 가지는 권한 목록을 정의합니다.
 * 사용자가 여러 역할을 가진 경우, 모든 역할의 권한을 합쳐서 적용합니다.
 *
 * @example
 * // 일반 직원의 권한 확인
 * const generalPermissions = ROLE_PERMISSIONS['general'];
 * // ['dashboard.read', 'notice.read', ...]
 *
 * @example
 * // HR의 권한 확인
 * const hrPermissions = ROLE_PERMISSIONS['role_hr'];
 * // ['dashboard.read', 'notice.read', 'notice.write', 'hr.manage', ...]
 */
export const ROLE_PERMISSIONS: Record<RoleId, PermissionKey[]> = {
  /**
   * 일반 직원 (general)
   *
   * 기본 직원 역할로 본인 정보만 조회/수정 가능
   * - 대시보드, 공지사항, 조직도 조회
   * - 본인 근로 정보 조회/수정
   * - 본인 팀 정보 조회
   * - 마스터데이터 조회
   */
  general: [
    'dashboard.read',
    'notice.read',
    'organization.read',
    'work.my.read',
    'work.my.write',
    'team.my.read',
    'master.read',
    'certificate.request',
    'evaluation.self',
  ],

  /**
   * TL (tl)
   *
   * 일반 직원 + MM 등록 권한
   * - general의 모든 권한
   * - MM 현황 조회
   * - MM 등록
   */
  tl: [
    'dashboard.read',
    'notice.read',
    'organization.read',
    'work.my.read',
    'work.my.write',
    'team.my.read',
    'mm.read',
    'mm.register',
    'master.read',
    'certificate.request',
    'evaluation.self',
  ],

  /**
   * 조직장 (org_manager)
   *
   * 팀장/실장/본부장으로 본인 조직 관리 권한
   * - general의 모든 권한
   * - 팀원 근로 정보 조회
   * - 근무/연차 승인
   * - 팀 관리
   * - MM 관리 전체
   */
  org_manager: [
    'dashboard.read',
    'notice.read',
    'organization.read',
    'work.my.read',
    'work.my.write',
    'work.team.read',
    'work.approve',
    'team.my.read',
    'team.my.manage',
    'mm.read',
    'mm.register',
    'mm.manage',
    'master.read',
    'certificate.request',
    'evaluation.self',
  ],

  // V111: hr 권한 매핑 제거 (role_hr 사용)

  /**
   * 승인자 (approver)
   *
   * 근무/연차 승인 처리 권한
   * - general의 모든 권한
   * - 팀원 근로 정보 조회
   * - 근무/연차 승인
   */
  approver: [
    'dashboard.read',
    'notice.read',
    'organization.read',
    'work.my.read',
    'work.my.write',
    'work.team.read',
    'work.approve',
    'team.my.read',
    'master.read',
    'certificate.request',
    'evaluation.self',
  ],

  /**
   * 임원 (executive)
   *
   * 전사 데이터 조회 권한 (수정 불가)
   * - 모든 정보 조회 가능
   * - 수정 권한 없음 (조회 전용)
   */
  executive: [
    'dashboard.read',
    'notice.read',
    'organization.read',
    'work.my.read',
    'work.my.write',
    'work.team.read',
    'work.all.read',
    'hr.employees.read',
    'hr.org.read',
    'hr.settings.read',
    'team.my.read',
    'team.all.read',
    'mm.read',
    'master.read',
    'certificate.request',
  ],

  // V111: super_admin 권한 매핑 제거 (system_admin 사용)

  /**
   * 시스템 관리자 (system_admin)
   *
   * 모든 권한 보유
   * - 시스템의 모든 기능 사용 가능
   * - 모든 데이터 조회/수정 가능
   */
  system_admin: [
    'dashboard.read',
    'notice.read',
    'notice.write',
    'organization.read',
    'organization.write',
    'work.my.read',
    'work.my.write',
    'work.team.read',
    'work.all.read',
    'work.approve',
    'hr.manage',
    'hr.employees.read',
    'hr.employees.write',
    'hr.org.read',
    'hr.org.write',
    'hr.settings.read',
    'hr.settings.write',
    'team.my.read',
    'team.my.manage',
    'team.all.read',
    'mm.read',
    'mm.register',
    'mm.manage',
    'master.read',
    'master.write',
    'certificate.request',
    'certificate.manage',
    'evaluation.self',
    'evaluation.manage',
    'system.admin',
  ],

  /**
   * 일반 직원 (role_te) - 신규 역할 (2025-12-08)
   *
   * 기본 직원 역할
   * - general 역할과 동일한 권한
   */
  role_te: [
    'dashboard.read',
    'notice.read',
    'organization.read',
    'work.my.read',
    'work.my.write',
    'team.my.read',
    'master.read',
    'certificate.request',
    'evaluation.self',
  ],

  /**
   * 테스트 리더 (role_tl) - 신규 역할 (2025-12-08)
   *
   * QA 테스트 리더 역할
   * - role_te의 모든 권한 + MM 관리
   */
  role_tl: [
    'dashboard.read',
    'notice.read',
    'organization.read',
    'work.my.read',
    'work.my.write',
    'team.my.read',
    'mm.read',
    'mm.register',
    'master.read',
    'certificate.request',
    'evaluation.self',
  ],

  /**
   * 관리자 (role_manager) - 신규 역할 (2025-12-08)
   *
   * 팀장/실장/본부장 역할
   * - org_manager와 동일한 권한
   */
  role_manager: [
    'dashboard.read',
    'notice.read',
    'organization.read',
    'work.my.read',
    'work.my.write',
    'work.team.read',
    'work.approve',
    'team.my.read',
    'team.my.manage',
    'mm.read',
    'mm.register',
    'mm.manage',
    'master.read',
    'certificate.request',
    'evaluation.self',
  ],

  /**
   * 인사 플래그 (role_hr) - 신규 역할 (2025-12-08)
   *
   * HR 메뉴 접근 플래그
   * - V133: 팀관리 메뉴 접근 권한 제거 (org_manager만 접근)
   *   - team.my.manage, work.team.read, work.approve 제거
   */
  role_hr: [
    'dashboard.read',
    'notice.read',
    'notice.write',
    'organization.read',
    'organization.write',
    'work.my.read',
    'work.my.write',
    'work.all.read',
    'hr.manage',
    'hr.employees.read',
    'hr.employees.write',
    'hr.org.read',
    'hr.org.write',
    'hr.settings.read',
    'hr.settings.write',
    'team.my.read',
    'team.my.manage',
    'team.all.read',
    'mm.read',
    'mm.register',
    'mm.manage',
    'master.read',
    'master.write',
    'certificate.request',
    'certificate.manage',
    'evaluation.self',
    'evaluation.manage',
  ],

  /**
   * 경영진 열람 (role_exec) - 신규 역할 (2025-12-08)
   *
   * 이사/대표 등 경영진 전용
   * - executive 역할과 동일한 권한
   */
  role_exec: [
    'dashboard.read',
    'notice.read',
    'organization.read',
    'work.my.read',
    'work.my.write',
    'work.team.read',
    'work.all.read',
    'hr.employees.read',
    'hr.org.read',
    'hr.settings.read',
    'team.my.read',
    'team.all.read',
    'mm.read',
    'master.read',
    'certificate.request',
  ],

  /**
   * V27: KR 회사 관리자 (role_admin_kr)
   *
   * system_admin과 동일한 기능 권한 보유
   * 단, 회사 스코프는 KR로 고정 (백엔드에서 강제 필터링)
   */
  role_admin_kr: [
    'dashboard.read',
    'notice.read',
    'notice.write',
    'organization.read',
    'organization.write',
    'work.my.read',
    'work.my.write',
    'work.team.read',
    'work.all.read',
    'work.approve',
    'hr.manage',
    'hr.employees.read',
    'hr.employees.write',
    'hr.org.read',
    'hr.org.write',
    'hr.settings.read',
    'hr.settings.write',
    'team.my.read',
    'team.my.manage',
    'team.all.read',
    'mm.read',
    'mm.register',
    'mm.manage',
    'master.read',
    'master.write',
    'certificate.request',
    'certificate.manage',
    'evaluation.self',
    'evaluation.manage',
    'system.admin',
  ],

  /**
   * V27: CN 회사 관리자 (role_admin_cn)
   *
   * system_admin과 동일한 기능 권한 보유
   * 단, 회사 스코프는 CN으로 고정 (백엔드에서 강제 필터링)
   */
  role_admin_cn: [
    'dashboard.read',
    'notice.read',
    'notice.write',
    'organization.read',
    'organization.write',
    'work.my.read',
    'work.my.write',
    'work.team.read',
    'work.all.read',
    'work.approve',
    'hr.manage',
    'hr.employees.read',
    'hr.employees.write',
    'hr.org.read',
    'hr.org.write',
    'hr.settings.read',
    'hr.settings.write',
    'team.my.read',
    'team.my.manage',
    'team.all.read',
    'mm.read',
    'mm.register',
    'mm.manage',
    'master.read',
    'master.write',
    'certificate.request',
    'certificate.manage',
    'evaluation.self',
    'evaluation.manage',
    'system.admin',
  ],

  /**
   * V29.14: 글로벌 스코프 확장 (role_global)
   *
   * 스코프 확장 전용 역할 - 권한 없음
   * 다른 역할(role_hr 등)과 조합하여 회사 스코프를 ALL로 확장
   */
  role_global: [],
};

/**
 * 라우트별 필요 권한 매핑
 *
 * 각 라우트 경로에 접근하기 위해 필요한 권한을 정의합니다.
 * 권한이 없는 경우 접근을 차단하고 대시보드로 리다이렉트합니다.
 *
 * @example
 * // 인사관리 페이지 접근에 필요한 권한 확인
 * const requiredPermission = ROUTE_PERMISSIONS['/hr/employees'];
 * // 'hr.manage'
 */
export const ROUTE_PERMISSIONS: Record<string, PermissionKey | PermissionKey[]> = {
  // 공통 메뉴 (모든 역할 접근 가능)
  '/dashboard': 'dashboard.read',
  '/notice': 'notice.read',
  '/organization': 'organization.read',

  // V83: 공지사항 (모든 역할 조회 가능, 작성은 권한 체크)
  '/announcements': 'notice.read',
  '/announcements/new': 'notice.write',
  '/announcements/:id': 'notice.read',
  '/announcements/:id/edit': 'notice.write',

  // 근로관리 (본인 정보는 모두 접근 가능)
  '/work/my-dashboard': 'work.my.read',
  '/work/schedule': 'work.my.read',
  // V151: '/work/my-leaves' 삭제됨 (내 휴가관리로 통합)
  '/my/leave': 'work.my.read', // 내 휴가관리
  '/work/my-approvals': 'work.my.read',

  // 승인관리 (승인 권한 필요)
  '/work/approvals': 'work.approve',

  // 인사관리 (HR 권한 필요, 임원은 조회만 가능)
  '/hr/employees': ['hr.employees.read', 'hr.manage'],
  '/hr/employment-status': ['hr.employees.read', 'hr.manage'],
  '/hr/org': ['hr.org.read', 'hr.manage'],
  '/hr/org-units': ['hr.org.read', 'hr.manage'], // 조직관리 (Chapter 4-1)
  '/hr/org-tree': ['hr.org.read', 'hr.manage'], // 조직도 (Chapter 4-3)
  '/hr/positions': ['hr.settings.read', 'hr.manage'], // 직위 관리 (Chapter 5-1)
  '/hr/job-titles': ['hr.settings.read', 'hr.manage'], // 직책 관리 (Chapter 6-1)
  '/hr/personnel-appointment': ['hr.employees.read', 'hr.manage'], // V51: 인사발령 (4탭)
  '/hr/promotion-candidates': ['hr.employees.read', 'hr.manage'], // 진급 후보 (리다이렉트)
  '/hr/transfers': ['hr.employees.read', 'hr.manage'], // 전배 관리
  '/hr/dashboard': ['hr.employees.read', 'hr.manage'], // HR 대시보드
  '/hr/leave-overview': ['hr.settings.read', 'hr.manage'], // 휴가 현황
  '/hr/settings': ['hr.settings.read', 'hr.settings.write', 'hr.manage'], // HR 설정 관리 (V60: role_exec 읽기 전용)
  '/hr/leave-settings': ['hr.settings.read', 'hr.manage'],
  '/hr/holidays': ['hr.settings.read', 'hr.manage'],
  '/hr/performance': ['hr.employees.read', 'hr.manage'],
  '/hr/employment-management': ['hr.employees.read', 'hr.manage'], // 고용관리 (V45)
  '/hr/terminated-employees': ['hr.employees.read', 'hr.manage'], // 퇴사자 관리 (V77)
  '/hr/leave-approvals': ['hr.employees.read', 'hr.manage'], // V60: role_exec 읽기 전용 추가
  // V70-S: 감사 대응 (KR 전사원) — 'hr.manage' 만 (role_exec 의 hr.*.read 로는 열리지 않음).
  //        실제 허용은 HrKrAuditAccessGuard + 서버에서 system_admin·role_admin_kr·KR role_hr 로 한 번 더 제한
  '/hr/company-work-status': 'hr.manage',
  '/hr/overtime-management': 'hr.manage',

  // V135: 조직관리 (조직장 또는 HR 권한 필요)
  // 기존 /team/* 경로는 /org-management/*로 변경
  // V136: 직책 레벨 기반 접근 제한은 별도 정책(orgManagementPolicies)에서 관리
  '/org-management/members': 'team.my.manage', // 조직원관리
  '/org-management/projects': 'team.my.manage', // 프로젝트 관리
  '/org-management/work-status': ['work.team.read', 'work.approve'], // 조직 근로현황
  '/org-management/leave': ['work.team.read', 'work.approve'], // 조직 휴가관리
  '/org-management/interviews': 'team.my.manage', // V136: 조직원면담

  // MM관리
  '/mm/overview': 'mm.read',
  '/mm/register': 'mm.register',

  // 마스터데이터 (조회는 모두 가능, 수정은 HR만)
  // Note: 직위/직책 관리는 HR 메뉴에서만 제공 (/hr/positions, /hr/job-titles)
  '/master/employment-types': 'master.read',
  '/master/work-types': 'master.read',
  '/master/work-schedule': 'master.read',
  '/master/leave-types': 'master.read',
  '/master/leave-policies': 'master.read',
  '/master/appointment-types': 'master.read',
  '/master/project-status': 'master.read',

  // 증명서
  '/certificates': 'certificate.request',
  '/hr/certificate-management': ['hr.employees.read', 'certificate.manage'], // V60: role_exec 읽기 전용

  // 평가
  '/my/evaluations': 'evaluation.self',
  '/org-management/evaluations': 'team.my.manage',
  '/hr/evaluation-templates': 'evaluation.manage', // V57.2

  // V234: Admin 라우트 (system.admin 전용)
  '/admin/roles': 'system.admin',
  // V70.27: 인력 현황 관리 (노출 조직 + 접근 사용자 설정)
  // ROUTE_PERMISSIONS 에 없는 경로는 canAccessRoute() 가 true 를 반환하므로 반드시 명시한다.
  '/admin/workforce-overview': 'system.admin',
  // V70 Tournament: 게임대회 설정 (system_admin 전용 — 사용자 지시 §3)
  // 사용자 측 /tournament/* 는 TournamentAccessGuard 가 BE 설정 기반 접근 제어 (ROUTE_PERMISSIONS 미등록 — 기본 허용 후 Guard 가 재차단)
  '/admin/tournament-settings': 'system.admin',
  // V70 Phase A: 참가신청 양식관리 (system_admin 전용)
  '/admin/tournament-applications': 'system.admin',
  // V70 엔글 룰렛 Phase 1 — Demo (system_admin 전용). 메뉴 숨김 + 직접 URL 접근 모두 차단.
  '/tournament/roulette/demo': 'system.admin',
};

/**
 * 권한 설명 매핑
 *
 * 각 권한에 대한 한국어 설명을 정의합니다.
 * UI에서 권한 정보를 표시할 때 사용합니다.
 *
 * @example
 * const description = PERMISSION_DESCRIPTIONS['notice.write'];
 * // '공지사항 작성 및 수정'
 */
export const PERMISSION_DESCRIPTIONS: Record<PermissionKey, string> = {
  // 공통
  'dashboard.read': '대시보드 조회',
  'notice.read': '공지사항 조회',
  'notice.write': '공지사항 작성 및 수정',
  'organization.read': '조직도 조회',
  'organization.write': '조직도 수정',

  // 근로관리
  'work.my.read': '내 근로 정보 조회',
  'work.my.write': '내 근로 정보 수정 (출퇴근 기록)',
  'work.team.read': '팀원 근로 정보 조회',
  'work.all.read': '전사 근로 정보 조회',
  'work.approve': '근무/연차 승인 처리',

  // 인사관리
  'hr.employees.read': '직원 정보 조회',
  'hr.employees.write': '직원 정보 수정',
  'hr.org.read': '조직 정보 조회',
  'hr.org.write': '조직 정보 수정',
  'hr.settings.read': '인사 설정 조회',
  'hr.settings.write': '인사 설정 수정',
  'hr.manage': '인사관리 전체 권한',

  // 팀관리
  'team.my.read': '내 팀 정보 조회',
  'team.my.manage': '내 팀 관리',
  'team.all.read': '전체 팀 정보 조회',

  // MM관리
  'mm.read': 'MM 현황 조회',
  'mm.register': 'MM 등록',
  'mm.manage': 'MM 관리 전체',

  // 마스터데이터
  'master.read': '마스터데이터 조회',
  'master.write': '마스터데이터 수정',

  // 증명서
  'certificate.request': '증명서 발급 신청',
  'certificate.manage': '증명서 관리 (인사팀)',

  // 평가
  'evaluation.self': '본인 평가 조회 및 자가평가 제출',
  'evaluation.manage': '평가 관리 (인사팀)',

  // 시스템
  'system.admin': '시스템 관리 전체',
};
